import { Component, Input, OnDestroy } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import { filter, mergeMap, take } from 'rxjs/operators';

import { DeleteDialogService } from '../../../core/services/dialog-services/delete-dialog.service';
import { PlaylistsService } from '../../../core/services/http-services/playlists.service';
import { PlaylistStateService } from '../../../core/services/screen-state/playlists-state.service';
import { UIService } from '../../../core/services/utils/ui.service';
import { AuthService } from '../../../core/services/auth/auth.service';
import { SchedulesService } from '../../../core/services/http-services/schedules.service';

import { PlaylistDTO } from '../../../core/models/view-models/playlist-models/playlist-dto.model';
import { Schedule } from '../../../core/models/entities/schedule.type';
import { LibraryEditItemTypeEnum } from '../../../core/enums/library-edit-item-type.enum';
import { ToastrMessageEnum } from '../../../core/enums/toastr-messages/toastr-message.enum';
import { PlaylistDialogMessagesEnum } from '../../../core/enums/toastr-messages/playlist-dialog-messages.enum';
import { DisplayPlaylistDTO } from '../../../core/models/view-models/playlist-models/display-playlist-dto.model';

@Component({
  selector: 'playlist',
  templateUrl: './playlist.component.html',
  styleUrls: ['./playlist.component.scss'],
  providers: [DeleteDialogService]
})
export class PlaylistComponent implements OnDestroy {

  @Input() playlist: PlaylistDTO;
  @Input() isAnyScreenPending: boolean;

  public userId: string;
  public isHover: Boolean = false;
  private subscriptions: Subscription = new Subscription();

  constructor(private readonly dataService: PlaylistsService,
    private readonly toastrService: ToastrService,
    private readonly deleteDialogService: DeleteDialogService,
    private readonly playlistStateService: PlaylistStateService,
    private readonly uiService: UIService,
    private readonly authService: AuthService,
    private readonly schedulesService: SchedulesService
  ) {

    if (authService.userId) {
      this.userId = authService.userId;
    } else {
      this.subscriptions.add(authService.getCaller()
        .subscribe(data => {
          this.userId = data.Id;
        }));
    }
  }

  copy(): void {
    this.dataService.copyPlaylist(this.playlist.Id)
      .pipe(take(1))
      .subscribe(([createdPlaylist, sourcePlaylist]) => {
        this.dataService.setFeaturesToLauncher(createdPlaylist, sourcePlaylist);
        this.openDuplicatedPlaylist(createdPlaylist);

        this.toastrService.success(
          `\'${this.playlist.PlaylistTitle}\' ${ToastrMessageEnum.SuccessfullyDuplicatedTitle}`,
          PlaylistDialogMessagesEnum.PlaylistDuplicationTitle
        );
      });
  }

  edit(): void {
    const data = _.cloneDeep(this.playlist);
    this.uiService.showLibraryEditPanel(data, LibraryEditItemTypeEnum.EditPlaylist);
  }

  play(): void {
    this.playlistStateService.changePlaylist(this.playlist, true);
  }

  delete(): void {
    this.subscriptions.add(this.schedulesService.getAllSchedules().subscribe((schedules: Schedule[]) => {
      const isScheduled: boolean = _.some(schedules, (s: Schedule) => s.PlaylistId === this.playlist.Id);
      this.showDeleteDialog(isScheduled);
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private showDeleteDialog(isScheduled: boolean): void {
    this.deleteDialogService.show(`${PlaylistDialogMessagesEnum.DeletingPlaylistTitle}${isScheduled ?
      PlaylistDialogMessagesEnum.PlaybackScheduleMessage : ''}`)
      .pipe(
        filter((result) => !!result),
        mergeMap(() => this.dataService.deletePlaylist(this.playlist.Id)),
        take(1)
      )
      .subscribe(() => {
        this.toastrService.success(
          `\'${this.playlist.PlaylistTitle}\' ${ToastrMessageEnum.SuccessfullyDeletedTitle}`,
          PlaylistDialogMessagesEnum.PlaylistDeletingTitle
        )
      });
  }

  private openDuplicatedPlaylist(createdPlaylist: DisplayPlaylistDTO): void {
    this.dataService.playlistCollectionChanged
      .pipe(take(1))
      .subscribe((playlists) => {
        playlists.forEach((playlist) => {
          if (playlist.Id === createdPlaylist.Id) {
            this.uiService.showLibraryEditPanel(playlist, LibraryEditItemTypeEnum.EditPlaylist);
          }
        })
      });
  }
}
